import React, {
  useState,
  FunctionComponent,
  Fragment,
  MouseEvent,
} from 'react';
import { connect } from 'react-redux';
import { Menu, MenuItem, IconButton } from '@mui/material';
import { Help as HelpIcon } from '@mui/icons-material';

import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';

import { Colors } from '../../../constants';
import { IAppState } from '../../../state/reducers';
import { Product } from '../../../state/product';
import { toggleEmailDialog } from '../../../state/supportEmailDialog';
import SupportEmailDialog from './SupportDialog/component';
import { selectHasDownloadPermission, selectIsUserExchangeVendor } from '../../../state/user/userOrgRole';

interface IStateProps {
  product: string;
  canDownload: boolean;
}

interface IDispatchProps {
  onOpenSupport: () => void;
}

type IProps = IStateProps & IDispatchProps;

const ProductKbMap: { [key: string]: string } = {
  [Product.Exchange]: 'https://thunder.catapultsports.com/hc/en-us/categories/360000130075-Vault-Exchange',
};

const consoleInstallationUrl: string = 'https://thunder.catapultsports.com/hc/en-us/articles/360001509475-Console-Installation';

const HelpButton = styled(IconButton)`
  color: ${Colors.WHITE_100} !important;
`;

const StyledMenuItem = styled(MenuItem)`
  color: ${Colors.BLACK_80};
  a {
    color: ${Colors.BLACK_80};
    text-decoration: none;
  }
`;

const HelpMenu: FunctionComponent<IProps> = (props: IProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isOpen, setOpen] = useState<boolean>(false);

  const toggleOpen = (e: MouseEvent) => {
    if (!anchorEl) {
      setAnchorEl(e.target as HTMLElement);
    }
    setOpen(!isOpen);
  };

  const openSupport = () => {
    const { onOpenSupport } = props;
    setOpen(false);
    onOpenSupport();
  };

  const { product, canDownload } = props;

  const knowledgeBase = ProductKbMap[product];
  const displayConsoleInstallation = product === Product.Exchange && canDownload;

  return (
    <>
      <SupportEmailDialog />
      <HelpButton
        onClick={toggleOpen}
        data-tip="Help"
        data-for="help-tooltip"
        data-auto="HelpBtn"
      >
        <HelpIcon />
      </HelpButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={isOpen}
        onClick={toggleOpen}
        onClose={toggleOpen}
      >
        <StyledMenuItem
          onClick={openSupport}
          data-auto="EmailSupport"
        >
          Email Support
        </StyledMenuItem>
        {knowledgeBase && (
          <StyledMenuItem
            data-auto="KnowledgeBaseLink"
          >
            <a
              href={knowledgeBase}
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit Knowledge Base
            </a>
          </StyledMenuItem>
        )}
        {displayConsoleInstallation && (
          <StyledMenuItem
            data-auto="ConsoleInstallationLink"
          >
            <a
              href={consoleInstallationUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Console Installation
            </a>
          </StyledMenuItem>
        )}
      </Menu>
      <ReactTooltip
        id="help-tooltip"
        delayShow={250}
        effect="solid"
      />
    </>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => ({
  product: state.product.target,
  canDownload: selectHasDownloadPermission(state),
});

const mapDispatchToProps: IDispatchProps = {
  onOpenSupport: toggleEmailDialog,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(HelpMenu);
